import { useCallback, useEffect, useState } from 'react'
import { Card, Col, Form, Row } from 'react-bootstrap'
import MenuItem from '@mui/material/MenuItem'
import { useDispatch } from 'react-redux'

/** TRANSLATION*/
import { useTranslation } from 'react-i18next'

/** STYLE */
import useStyles from 'features/setting/default-users/access-channels/style'

/** API */
import AccessChannelApi from 'api/setting/default-users/access-channel.api'

/** UTILS || SLICE */
import { submitModal, unSubmitModal, showModal, resetModal } from 'app/slice/modal.slice'
import { isCreate, isDelete, isUpdate, isView } from 'utils/verification.utils'

/** COMPONENT */
import { swalActive } from 'component/Alert/Swal'
import ButtonCustom from 'component/Button/ButtonCustom'
import HeaderCard from 'component/CardCustom/HeaderCard'
import InputTextField from 'component/Input/InputTextField'
import InputTextSearch from 'component/Input/InputTextSearch'
import ModalCustom from 'component/ModalCustom/ModalCustom'
import TableCustom from 'component/Table/TableCustom'
import { notiSuccess, notiError } from 'component/notifications/notifications'
import TableRowCommon from 'component/Table/TableRowCommon'
import FilterSelect from 'component/Select/FilterSelect'
import InputCheckbox from 'component/Input/InputCheckbox'
import { TRUE_FALSE } from 'constants/status'
import AlertModal from 'new-components/modal/alert-modal'
import SnackbarComponent from 'new-components/snackbar/snackbar-component'
import PageModal from 'new-components/modal/page-modal'
import TextInput from 'new-components/inputs/text-input'
import CheckboxButton from 'new-components/buttons/checkbox-button'
import SwitchButton from 'new-components/buttons/switch-button'
import { Box } from '@mui/material'

const permissions = {
  isCreate: isCreate(),
  isDelete: isDelete(),
  isUpdate: isUpdate(),
  isView: isView()
}

const initStateErrorMessage = {
  ACCESS_CHANNEL_NAME: ``,
  IS_DUPLICATE_ACCESS_CHANNEL_NAME: ``,
  ACCESS_CHANNEL_NAME_STRING_BASE: ``,
  ACCESS_CHANNEL_NAME_STRING_EMPTY: ``,
  ACCESS_CHANNEL_NAME_MAXIMUM_LENGTH: ``,
  ACCESS_CHANNEL_NAME_ANY_REQUIRED: ``,

  ACCESS_CHANNEL_NAME_EN: ``,
  IS_DUPLICATE_ACCESS_CHANNEL_NAME_EN: ``,
  ACCESS_CHANNEL_NAME_EN_STRING_BASE: ``,
  ACCESS_CHANNEL_NAME_EN_STRING_EMPTY: ``,
  ACCESS_CHANNEL_NAME_EN_MAXIMUM_LENGTH: ``,
  ACCESS_CHANNEL_NAME_EN_ANY_REQUIRED: ``
}
export default function AccessChannels() {
  const classes = useStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [accessChannels, setChannels] = useState([])
  const [accessChannelId, setAccessChannelId] = useState(Number)
  const [channelName, setChannelName] = useState('')
  const [isOther, setIsOther] = useState<TRUE_FALSE>(TRUE_FALSE.FALSE)
  const [accessChannelStatus, setChannelStatus] = useState('')

  const [errorMessage, setErrorMessage] = useState(initStateErrorMessage)

  /** PAGINATION  */
  const [page, setPage] = useState(1)
  const [pageLimit, setPageLimit] = useState(10)
  const [rowCount, setRowCount] = useState(0)
  const [sortBy, setSortBy] = useState('createAt')
  const [sortType, setSortType] = useState('ASC')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('ALL')

  const [isOpenAlertModal, setIsOpenAlertModal] = useState(false)
  const [statusAlertModal, setStatusAlertModal] = useState('')
  const [id, setId] = useState<number>(0)
  const [name, setName] = useState('')
  const [isOpenSnack, setIsOpenSnack] = useState(false)
  const [typeSnack, setTypeSnack] = useState<any>("success")
  const [labelSnack, setLabelSnack] = useState('')

  const [isPopup, setIsPopup] = useState(false)

  const loadData = useCallback(async () => {

    let condition: any = {}
    if (page) condition = { ...condition, page: page }
    if (pageLimit) condition = { ...condition, pageLimit: pageLimit }
    if (search) condition = { ...condition, search: search }
    if (sortBy) condition = { ...condition, sortBy: sortBy }
    if (sortType) condition = { ...condition, sortType: sortType }
    if (status) condition = { ...condition, status: status === 'ALL' ? '' : status }

    const res = await AccessChannelApi.findAll(condition)
    if (res.status === 200) {
      setRowCount(res.headers['x-total'])
      setChannels(res.data)
    }
  }, [page, pageLimit, search, sortBy, sortType, status])

  useEffect(() => {
    loadData()
  }, [loadData])

  const submit = () => {
    if (!channelName) return setErrorMessage({ ...errorMessage, ...{ ACCESS_CHANNEL_NAME: t('ACCESS_CHANNEL.MESSAGE.ACCESS_CHANNEL_NAME') } })

    dispatch(submitModal())
    AccessChannelApi.create(channelName, isOther)
      .then((res: any) => {
        if (res.status !== undefined && res.status === 201) {
          notiSuccess(t('ACCESS_CHANNEL.MESSAGE.SUCCESS.CREATE'), '', null, null)
          resetForm()
          loadData()
          dispatch(resetModal())
        } else {
          const err = res.response.data
          setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`ACCESS_CHANNEL.MESSAGE.${err.message}`) } })
          dispatch(unSubmitModal())
        }
      })
      .catch((e) => {
        const err = e.response.data
        setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`ACCESS_CHANNEL.MESSAGE.${err.message}`) } })
        dispatch(unSubmitModal())
      })
  }

  const submitEdit = () => {
    if (!channelName) return setErrorMessage({ ...errorMessage, ...{ ACCESS_CHANNEL_NAME: t(`ACCESS_CHANNEL.MESSAGE.ACCESS_CHANNEL_NAME`) } })

    dispatch(submitModal())
    AccessChannelApi.update(accessChannelId, {
      channelName,
      isOther,
      status: accessChannelStatus
    })
      .then((res: any) => {
        if (res.status !== undefined && res.status === 200) {
          notiSuccess(t(`ACCESS_CHANNEL.MESSAGE.SUCCESS.UPDATE`), '', null, null)
          resetForm()
          loadData()
          dispatch(resetModal())
        } else {
          const err = res.response.data
          setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`ACCESS_CHANNEL.MESSAGE.${err.message}`) } })
          dispatch(unSubmitModal())
        }
      })
      .catch((e) => {
        const err = e.response.data
        setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`ACCESS_CHANNEL.MESSAGE.${err.message}`) } })
        dispatch(unSubmitModal())
      })
  }

  const clearErrorMessage = () => {
    setErrorMessage(initStateErrorMessage)
  }

  const resetForm = () => {
    setAccessChannelId(0)
    setChannelName('')
    setChannelStatus('')
    setIsOther(TRUE_FALSE.FALSE)
    setIsPopup(false)
    clearErrorMessage()
  }

  const onActive = () => {
    if (id) {
      AccessChannelApi.updateStatus(id, statusAlertModal).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("success")
          setLabelSnack(t('ACCESS_CHANNEL.MESSAGE.SUCCESS.UPDATE_STATUS'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("error")
          setLabelSnack(t('ACCESS_CHANNEL.MESSAGE.ERROR'))
        }
      })
    }
  }
  const onDelete = () => {
    if (id) {
      AccessChannelApi.remove(id).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("success")
          setLabelSnack(t('ACCESS_CHANNEL.MESSAGE.SUCCESS.DELETE'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("error")
          setLabelSnack(t('ACCESS_CHANNEL.MESSAGE.ERROR'))
        }
      })
    }
  }

  const onEdit = (data: any) => {
    const { accessChannelId: acId, channelName: acName, isOther, status: acStatus } = data
    setAccessChannelId(acId)
    setChannelName(acName)
    setIsOther(isOther)
    setChannelStatus(acStatus)
    setIsPopup(true)
  }

  const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
    setSortType(sortTypeVal)
    setSortBy(sortByVal)
  }

  const handleChangePage = (val: number) => {
    setPage(val)
  }

  const handleChangeRowsPerPage = (limit: number) => {
    setPageLimit(limit)
  }
  const handleChangeStatus = (statusVal: string) => {
    setStatus(statusVal)
  }
  const headCells = [
    { id: 'no', disablePadding: false, label: '#' },
    { id: 'channelName', disablePadding: false, label: t('ACCESS_CHANNEL.TABLE.CELL.ACCESS_CHANNEL_NAME') },
    { id: 'status', disablePadding: false, label: t('ACCESS_CHANNEL.TABLE.CELL.STATUS') },
    { id: 'updatedBy', disablePadding: false, label: t('ACCESS_CHANNEL.TABLE.CELL.UPDATED_BY') },
    { id: 'action', disablePadding: false, label: 'Action' }
  ]

  const renderData = (objData: any, no: number) => {
    no = page * pageLimit - pageLimit + no + 1
    const { accessChannelId: acId, channelName: acName, updatedBy, updatedAt, status: acStatus } = objData
    const statusBtnActive = acStatus === 'ACTIVE' ? true : permissions.isUpdate.disabled
    const statusBtnInActive = acStatus === 'INACTIVE' ? true : permissions.isUpdate.disabled
    const objRenderData = {
      key: acId,
      id: acId,
      obj: objData,
      columns: [
        { option: 'TEXT', align: 'center', label: no },
        { option: 'TEXT', align: 'left', label: acName },
        { option: 'STATUS', align: 'center', label: acStatus },
        { option: 'UPDATE_BY', align: 'center', label: { updatedBy: updatedBy, updatedAt: updatedAt } },
        {
          option: 'ACTION',
          align: 'center',
          label: 'action',
          values: [
            { option: 'STATUS_ACTIVE', label: t(`STATUS.ACTIVE`), disabled: statusBtnActive },
            { option: 'STATUS_INACTIVE', label: t(`STATUS.INACTIVE`), disabled: statusBtnInActive },
            { option: 'DIVIDER', label: '', disabled: false },
            { option: 'EDIT', label: t(`BUTTON.EDIT`), disabled: permissions.isUpdate.disabled },
            { option: 'DELETE', label: t(`BUTTON.DELETE`), disabled: permissions.isDelete.disabled }
          ]
        }
      ]
    }
    return (
      <TableRowCommon
        {...objRenderData}
        onactive={() => handleSubmitModal(Number(objRenderData.id), 'ACTIVE', objRenderData.obj)}
        oninactive={() => handleSubmitModal(Number(objRenderData.id), 'INACTIVE', objRenderData.obj)}
        onedit={() => onEdit(objRenderData.obj)}
        ondelete={() => handleSubmitModal(Number(objRenderData.id), 'DELETE', objRenderData.obj)}
      />
    )
  }

  const handleSubmitModal = (id: number, status: string, obj?: any) => {
    setStatusAlertModal(status)
    setId(id)
    setIsOpenAlertModal(true)
    setName(obj.channelName)
  }
  return (
    <div className={classes.root}>
      <AlertModal
        isOpen={isOpenAlertModal}
        title={t(`ACCESS_CHANNEL.ALERT.${statusAlertModal === 'DELETE' ? 'TITLE_DELETE' : 'TITLE_ACTIVE'}`)}
        message={t(`ACCESS_CHANNEL.ALERT.${statusAlertModal === 'DELETE' ? 'CONFIRM_DELETE_' : 'CONFIRM_STATUS'}`, { accessChannelName: name, status: t(`ACCESS_CHANNEL.ALERT.${statusAlertModal}`) })}
        type={statusAlertModal === 'DELETE' ? 'error' : 'warning'}
        handleClose={() => setIsOpenAlertModal(false)}
        onCancel={{ onClick: () => setIsOpenAlertModal(false), title: t('BUTTON.CANCEL') }}
        onSubmit={{ onClick: statusAlertModal === 'DELETE' ? onDelete : onActive, title: t('BUTTON.SUBMIT') }}
      />
      <SnackbarComponent
        onOpen={isOpenSnack}
        onClose={() => setIsOpenSnack(false)}
        label={labelSnack}
        type={typeSnack}
        transform={`translateX(calc(100vw - 100% - 32px)) translateY(calc(-100vh + 100% + ${window.innerHeight - 50}px))`}
        autoHideTime={3000}
      />
      <Card className="border-0 h-100">
        <HeaderCard text={t('ACCESS_CHANNEL.TITLE')} />
        <Card.Body>
          <Card.Title className={'mb-0 pl-xl-3'}>
            <Row className="align-items-center">
              <Col md={8}>
                <Row className="flax-row">
                  <Col sm={12} md={6} xl={4} className="pt-2">
                    <InputTextSearch
                      keyInput={'search'}
                      label={t('ACCESS_CHANNEL.SEARCH')}
                      value={search}
                      onchange={(event) => {
                        setSearch(event.target.value)
                        setPage(1)
                      }}
                      onClear={(event) => {
                        setSearch('')
                        setPage(1)
                      }}
                    />
                  </Col>
                  <Col sm={12} md={6} xl={4} className="pl-md-0 pt-2">
                    <FilterSelect
                      onchange={(event) => {
                        const value = event.target.value
                        if (value) {
                          handleChangeStatus(value)
                          setPage(1)
                        }
                      }}
                      renderValue={() => `${t('STATUS.LABEL')}: ${t(`STATUS.${status}`)}`}
                      label={t('STATUS.LABEL')}
                      selectId="select-status"
                      value={status}
                      labelId="label-status"
                      options={[
                        <MenuItem key="1" value="ALL">
                          {t('STATUS.ALL')}
                        </MenuItem>,
                        <MenuItem key="2" value="ACTIVE">
                          {t('STATUS.ACTIVE')}
                        </MenuItem>,
                        <MenuItem key="3" value="INACTIVE">
                          {t('STATUS.INACTIVE')}
                        </MenuItem>
                      ]}
                    />
                  </Col>
                </Row>
              </Col>
              <Col md={4} className="mt-2 mt-md-auto">
                <div className="pr-xl-2">
                  <ButtonCustom mode="add" disabled={permissions.isCreate.disabled} onClick={() => setIsPopup(true)} textButton={t('ACCESS_CHANNEL.BUTTON.ADD')} className="w-auto ml-auto d-flex mt-auto" />
                </div>
              </Col>
            </Row>
          </Card.Title>

          <div className={'mt-3 pl-xl-3 pr-xl-2'}>
            <TableCustom
              page={page}
              pageLimit={pageLimit}
              sortType={sortType}
              sortBy={sortBy}
              onSort={onRequestSort}
              setPageLimit={handleChangeRowsPerPage}
              setPage={handleChangePage}
              rowCount={rowCount}
              headCells={headCells}
              rowsData={accessChannels.map((val, i) => {
                return renderData(val, i)
              })}
            />
          </div>
        </Card.Body>
      </Card>

      <PageModal
        title={accessChannelId ? t('ACCESS_CHANNEL.TITLE_UPDATE') : t('ACCESS_CHANNEL.TITLE_CREATE')}
        isOpen={isPopup}
        onCancel={{ onClick: () => resetForm() }}
        onSubmit={{ onClick: () => accessChannelId ? submitEdit() : submit() }}
        handleClose={resetForm}
        size={'XS'}
      >
        <Box className="py-2">
          {accessChannelId ? (
            <Form className="mx-0 mt-0 mb-3 p-0  d-inline-flex w-100">
              <Form.Label className=" mr-auto">{t('INPUT.LABEL_STATUS')}</Form.Label>
              <SwitchButton
                onChange={() => setChannelStatus(accessChannelStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE')}
                checked={accessChannelStatus === 'ACTIVE' ? true : false}
              />
            </Form>
          ) : (
            ''
          )}

          <Box display={'flex'} flexDirection={'column'} gap={'16px'}>
            <TextInput
              fullWidth
              onChange={(event) => {
                setChannelName(event.target.value)
                clearErrorMessage()
              }}
              value={channelName}
              label={t('ACCESS_CHANNEL.INPUT.ACCESS_CHANNEL_NAME')}
              helperText={
                errorMessage.ACCESS_CHANNEL_NAME ||
                errorMessage.IS_DUPLICATE_ACCESS_CHANNEL_NAME ||
                errorMessage.ACCESS_CHANNEL_NAME_STRING_BASE ||
                errorMessage.ACCESS_CHANNEL_NAME_STRING_EMPTY ||
                errorMessage.ACCESS_CHANNEL_NAME_MAXIMUM_LENGTH ||
                errorMessage.ACCESS_CHANNEL_NAME_ANY_REQUIRED
              }
              required={true}
            />
            <Box padding={'0 12px'}>
              <CheckboxButton
                key={`checkbox-input`}
                checked={isOther === TRUE_FALSE.TRUE ? true : false}
                label={t('ACCESS_CHANNEL.INPUT.SPECIFIED')}
                onChange={(e) => {
                  setIsOther(e?.target?.checked || false ? TRUE_FALSE.TRUE : TRUE_FALSE.FALSE)
                }}
              />
            </Box>
          </Box>
        </Box>
      </PageModal>

    </div>
  )
}
